import { PRIMARY_MARKETS } from '@/constants/markets';

export const createCompanyCanonicalUrl = ({
  primaryIndustrySlug,
  companyNameSlug,
  market,
  exchangeTicker,
}: {
  primaryIndustrySlug: string | undefined | null;
  companyNameSlug: string | undefined | null;
  market: string | undefined | null;
  /**
   * Note exchangeTicker is a special combination of a friendly exchange name and a ticker symbol
   * This does not map to uniqueSymbolSlug and can only be obtained by deconstructing canonicalUrl
   */
  exchangeTicker: string | undefined | null;
}) => {
  return companyNameSlug && market && exchangeTicker && primaryIndustrySlug
    ? `/stocks/${market}/${primaryIndustrySlug}/${exchangeTicker}/${companyNameSlug}`
    : null;
};

export const getNarrativeCommunityMeta = ({
  companyName,
  uniqueSymbol,
  primaryIndustryLabel,
  market,
  coverImageUrl,
}: {
  companyName: string | undefined | null;
  uniqueSymbol: string | undefined | null;
  coverImageUrl: string | undefined | null;
  primaryIndustryLabel: string | undefined | null;
  market: string | undefined | null;
}) => {
  const primaryMarket = market ? PRIMARY_MARKETS[market] : null;
  const marketDemonym = primaryMarket?.demonym;
  const marketExchange = primaryMarket?.mainExchange;

  const getSeoTitle = () => {
    if (companyName && uniqueSymbol)
      return `${companyName} (${uniqueSymbol}) Investor Community`;

    if (primaryIndustryLabel)
      return [
        marketDemonym,
        marketExchange ? `(${marketExchange})` : null,
        primaryIndustryLabel,
        'Community',
      ]
        .filter(Boolean)
        .join(' ');

    if (marketDemonym || marketExchange)
      return [
        marketDemonym,
        marketExchange ? `(${marketExchange})` : null,
        'Investor Community',
      ]
        .filter(Boolean)
        .join(' ');

    return 'Community';
  };

  const getSeoDescription = () => {
    if (companyName)
      return `Discover and discuss ${companyName} investment opportunities. Use our narrative toolset to guide your own investment decision.`;

    if (primaryIndustryLabel && marketDemonym)
      return `Discover and discuss investment opportunities within the ${marketDemonym} ${primaryIndustryLabel} Sector.`;

    if (marketDemonym)
      return `Discover and discuss investment opportunities within the ${marketDemonym} Market community.`;

    return 'Discover and discuss investment opportunities. Use our narrative toolset to guide your own investment decisions.';
  };

  return {
    seoTitle: getSeoTitle(),
    seoDescription: getSeoDescription(),
    seoImageUrl:
      coverImageUrl ||
      'https://simplywall.st/static/images/narratives/community-head-image.png',
  };
};

export const getNarrativeCommunityHiddenTitle = ({
  companyName,
  primaryIndustryLabel,
  market,
}: {
  companyName: string | undefined | null;
  primaryIndustryLabel: string | undefined | null;
  market: string | undefined | null;
}) => {
  const primaryMarket = market ? PRIMARY_MARKETS[market] : null;
  const marketDemonym = primaryMarket?.demonym;

  if (companyName) return 'Community Investing Ideas';

  if (primaryIndustryLabel)
    return [primaryIndustryLabel, marketDemonym, 'Investing Ideas']
      .filter(Boolean)
      .join(' ');

  if (marketDemonym) return `${marketDemonym} Investing Ideas`;

  return 'Community Investing Ideas';
};
